// 🗣️ 단타탭 '쉬운 읽기' 룰 레이어 — 알림/시장 상태의 숫자를 '말'로 통역한다.
// 전부 결정론적 룰(if-then 문장 사전) — AI 호출 없음(알림은 초 단위, AI는 느리고 비싸고 비결정적).
// 데이터는 이미 클라에 있다(backdrop·idxTrend·impact). 성적표 문화대로, 해석이 붙은 알림도 알림 기록으로 채점해 문장 사전을 보정한다.
import { DtAlert } from './alerts';
import { DaytradeBackdrop } from './types';

// ① 알림 해석 한 줄 — kind × '지금 상황'(지수 방향·수급 대치·모델 진행률)을 조합해 "그래서 뭐?"를 답한다.
//    idxUp: +1 지수 오르는 중 / −1 내리는 중 / 0 방향 불명(alerts.idxTrend).
export function interpretAlert(a: DtAlert, bd: DaytradeBackdrop | null | undefined, idxUp: number): string | null {
  const imp = bd?.impact;
  const cheap = !!(imp && imp.progress != null && imp.progress < 70);   // 수급 대비 지수가 '덜 빠진'(비싼) 상태
  switch (a.kind) {
    case 'flow_sell':
    case 'flow_buy': {
      const selling = a.kind === 'flow_sell' || a.fdir === 'sell';
      if (selling) {
        if (idxUp > 0) return cheap
          ? '파는데 지수는 오히려 오름 — 개인이 받아내는 반등이라 힘이 약해요. 수급 대비 지수가 아직 덜 빠진 상태, 따라 사지 말고 관망.'
          : '큰손이 파는데 지수는 버티는 중 — 개인이 받는 반등. 힘 약하니 롱 추격 주의.';
        if (idxUp < 0) return '팔면서 지수도 눌리는 중 — 매도가 먹히고 있어요. 롱 진입은 회피, 반등 확인 후에.';
        return '큰손이 파는 중 — 매도가 계속되면 지수가 눌립니다. 롱은 신중, 지수 방향 확인.';
      }
      if (idxUp > 0) return '사면서 지수도 오름 — 정방향 매수세. 눌림 오면 노려볼 자리.';
      if (idxUp < 0) return '사는데 지수는 눌림 — 저가 매집일 수 있어요. 바닥 다지면 관찰.';
      return '큰손이 담는 중 — 지수가 따라 오르면 정방향 신호.';
    }
    case 'flowearly':
      return '아직 확정 전 선행 신호 — 맞으면 남보다 빨리 잡고, 틀리면 헛불. 소액·확인 후 대응.';
    case 'futflow':
      return a.fdir === 'buy'
        ? '외인이 선물을 담는 중 — 선물은 지수를 앞서요. 반등/상승을 먼저 알리는 신호일 수 있어요(잠정).'
        : '외인이 선물을 던지는 중 — 선물은 지수를 앞서요. 하락을 먼저 알리는 신호일 수 있어요(잠정).';
    case 'leaderbreak': {
      const down = (a.dir ?? 0) < 0;
      return down
        ? '대형주가 지수보다 먼저 빠지는 중 — 지수 하락을 앞서는 신호일 수 있어요(미확정).'
        : '대형주가 먼저 반등 — 지수 저점 신호일 수 있어요(미확정). 확인 필요.';
    }
    case 'marketmove':
      return a.msg.includes('급락')
        ? '지수가 빠르게 하락 — 추격 매도 말고 반등·지지 확인부터.'
        : '지수가 빠르게 상승 — 추격 매수 주의, 눌림 기다리는 게 안전.';
    case 'regime':
      return a.verdict === '역풍'
        ? '시장 배경이 롱에 불리로 바뀜 — 단타 크기 줄이고 신중하게.'
        : a.verdict === '우호'
        ? '시장 배경이 롱에 유리로 바뀜 — 진입 자리 노려볼 만해요.'
        : null;
    case 'stockflow':
    case 'stance': {
      const buy = a.msg.includes('담') || a.msg.includes('순매수') || a.msg.includes('받');
      return buy
        ? '이 종목에 큰손이 붙는 중 — 개인만 받는 랠리가 아니면 힘이 실려요.'
        : '이 종목 큰손이 빠지는 중 — 개인만 받으면 물량받이 위험, 진입 신중.';
    }
    case 'leader':
      return '주도주 강세 — 시장을 끄는 종목. 다만 이미 오른 뒤면 추격 주의.';
    default:
      return null;
  }
}

// ② 숫자 → 판정어 — 임계를 아는 숫자에 '좋다/나쁘다'를 병기(61%가 좋은 건지 모를 때). 칩 텍스트에 붙일 짧은 꼬리.
export function chipJudgment(text: string): string | null {
  const prog = text.match(/(\d+)%\s*진행/);
  if (prog) {
    const n = Number(prog[1]);
    if (n < 70) return '덜 빠짐';           // 수급 대비 지수가 아직 덜 반영 → 여력(①해석과 동일 임계 70)
    if (n >= 90) return '거의 다 반영';
    return null;
  }
  const str = text.match(/체결강도\s*(\d+)/);
  if (str) {
    const n = Number(str[1]);
    if (n >= 120) return '매수 우위';
    if (n <= 80) return '매도 우위';
  }
  return null;
}

// ③ '지금 한 문장' 시장 브리핑 — 흩어진 신호(수급·국면·모델·장성격)를 앱이 대신 종합해 한 줄로. AI 없음.
export function marketBrief(bd: DaytradeBackdrop | null | undefined): { text: string; tone: 'warn' | 'good' | 'neutral'; reasons: string[] } | null {
  if (!bd || !bd.market_active) return null;
  const k = bd.markets?.KOSPI;
  const f = k?.foreign ?? 0, i = k?.institution ?? 0, ind = k?.individual ?? 0;
  const idx = bd.kospi_pct ?? 0;
  const imp = bd.impact;
  const eok = (v: number) => { const a = Math.abs(v); const s = v >= 0 ? '+' : '−'; return a >= 10000 ? `${s}${(a / 10000).toFixed(1)}조` : `${s}${Math.round(a).toLocaleString('ko-KR')}억`; };
  const fut = bd.futures?.foreign ?? null;   // A: 외인 선물 방향(계약)
  const reasons: string[] = [];
  if (k) reasons.push(`외국인 ${eok(f)}·기관 ${eok(i)}·개인 ${eok(ind)}`);
  if (fut != null) reasons.push(`외인 선물 ${fut >= 0 ? '+' : ''}${Math.round(fut).toLocaleString('ko-KR')}계약`);
  if (imp?.progress != null) reasons.push(`수급모델 ${Math.round(imp.progress)}% 반영`);
  if (bd.character?.character) reasons.push(bd.character.character);

  // 큰손 대량 매도 + 지수 상승 = '외상 반등'(오늘 혼란의 정확한 국면). 선물 방향으로 진위 가름(A).
  if (f + i <= -3000 && idx > -1.0) {
    const futTag = fut != null && fut <= -5000
      ? ' 선물까지 파는 양매도라 반등 신뢰가 낮아요 —'
      : fut != null && fut >= 5000
      ? ' 다만 선물은 사는 중이라 방향 전환 가능성은 지켜볼 만 —'
      : '';
    return { text: `큰손은 팔고 개인이 받아내는 반등 —${futTag} 수급 대비 지수가 비싼 편이라 신규 진입은 관망을 권해요.`, tone: 'warn', reasons };
  }
  // 국면 역풍
  if (bd.verdict === '역풍') {
    // 오늘 장 성격 카드가 이미 copy를 보여줌 — 같은 문장 중복 노출 제거(세로 다이어트 07-07)
    return { text: '오늘은 롱에 불리한 배경 — 단타는 크기 줄이고 신중하게.', tone: 'warn', reasons };
  }
  // 우호 + 큰손 매수
  if (bd.verdict === '우호' && f + i >= 1000) {
    return { text: '큰손이 담고 배경도 받쳐주는 장 — 눌림 오면 진입 노려볼 만해요.', tone: 'good', reasons };
  }
  // 큰손 매수(국면 무관)
  if (f + i >= 3000) {
    return { text: '큰손이 순매수 중 — 지수가 따라 오르면 정방향. 눌림 자리 관찰.', tone: 'good', reasons };
  }
  const copy = bd.character?.copy;
  return { text: copy || '혼조 — 뚜렷한 우위 없음, 방향 확인 후 대응.', tone: 'neutral', reasons };
}

// ④ 용어 사전 — 폰에선 hover 툴팁이 안 떠서, 탭하면 뜨는 바닥시트용. 핵심 용어만 짧게.
export const GLOSSARY: { term: string; desc: string }[] = [
  { term: '수급', desc: '외국인·기관·개인이 그날 얼마나 사고팔았나. 큰손(외국인·기관)이 사면 오를 힘, 팔면 내릴 힘.' },
  { term: '눌림목', desc: '오르던 종목이 잠깐 쉬며 내려온 자리. 추세가 살아있으면 여기서 다시 오를 확률이 높아 매수 자리로 봄.' },
  { term: 'VWAP', desc: '거래량 가중 평균가. 오늘 이 종목을 산 사람들의 평균 매입가 ― 이 위면 매수세 우위, 아래면 매도세 우위.' },
  { term: '체결강도', desc: '사자 체결 vs 팔자 체결 비율(100=중립). 120↑이면 매수가 세게 들어오는 중, 80↓이면 매도 우위.' },
  { term: '진행률', desc: '수급모델이 추정한 낙폭 대비 지수가 얼마나 내려왔나. 낮으면(덜 빠짐) 더 빠질 여력, 높으면 이미 반영.' },
  { term: '수급모델', desc: '외국인이 이만큼 팔면 지수가 대략 몇 % 빠진다는 과거 회귀 추정. 신뢰도가 높을수록 참고할 만함.' },
  { term: '큰손 매수 시작', desc: '큰손 매매가 기준값에 닿기 전, 유입 속도로 미리 잡는 선행 신호. 빠른 대신 오탐 가능(잠정). 매도 쪽은 "큰손 매도 시작".' },
  { term: '주도주 바스켓', desc: '시총 상위 대형주 묶음. 이들이 지수보다 먼저 움직이면 지수 방향을 앞서는 신호로 봄.' },
  { term: '역풍/우호', desc: '오늘 시장 배경이 롱(매수)에 불리(역풍)한지 유리(우호)한지. 같은 신호라도 배경에 따라 승률이 다름.' },
  { term: '물량받이', desc: '큰손은 파는데 개인만 받아 오르는 상태. 힘이 약해 되밀릴 위험 ― 추격 매수 주의.' },
  { term: '상한가 릴레이', desc: '오늘 크게 오른 종목이 다음 날·다음 종목으로 순환하는 흐름.' },
  { term: '다지기', desc: '급락 후 바닥에서 가격이 옆으로 눌러앉으며 매물을 소화하는 구간. 반등 직전일 때가 많음.' },
];
export function glossaryOf(term: string): string | null {
  return GLOSSARY.find((g) => g.term === term)?.desc ?? null;
}

// ⑤ 표시 이름 사전(TERMS.md 08-08) — 화면 글자만 바꾸는 창구. **키(왼쪽)는 서버·로직이 쓰는 값이라 절대 바꾸지 말 것**
//    (scorer.py가 내려주는 signal 값, portfolio.py의 pullback_grade 값 — 비교·필터·정렬이 전부 이 값으로 돈다).
//    화면에 글자를 찍는 자리에서만 sigLabel()/gradeLabel()을 통과시킨다.
export const SIGNAL_LABEL: Record<string, string> = {
  '신규진입': '초기',
  '완벽한추세': '매우 강함',
  '강한추세': '강함',
  '추세형성': '형성 중',
  '단기추세': '짧음',
};
export function sigLabel(sig: string | null | undefined): string {
  if (!sig) return '—';
  return SIGNAL_LABEL[sig] ?? sig;
}

export const GRADE_LABEL: Record<string, string> = {
  '진입적기': '지금 살 만함',
};
export function gradeLabel(g: string | null | undefined): string {
  if (!g) return '—';
  return GRADE_LABEL[g] ?? g;
}
